import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Fingerprint, ScanFace, ShieldCheck, Lock } from "lucide-react";
import { useBiometricAuth } from "@/hooks/useBiometricAuth";
import { hapticSuccess, hapticError } from "@/lib/despiaHaptics";

interface BiometricLockScreenProps {
  onUnlock: () => void;
  onSkip?: () => void;
}

export function BiometricLockScreen({ onUnlock, onSkip }: BiometricLockScreenProps) {
  const { biometryType, authenticate, getBiometryName } = useBiometricAuth();
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-trigger biometric on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      handleAuthenticate();
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  const handleAuthenticate = async () => {
    if (isAuthenticating) return;
    
    setIsAuthenticating(true);
    setError(null);

    try {
      const success = await authenticate();
      
      if (success) {
        hapticSuccess();
        onUnlock();
      } else {
        hapticError();
        setError("Authentification échouée");
      }
    } catch (err) {
      hapticError();
      setError("Erreur lors de l'authentification");
    } finally {
      setIsAuthenticating(false);
    }
  };

  const Icon = biometryType === "face" ? ScanFace : Fingerprint;

  return (
    <div className="fixed inset-0 z-[100] bg-background flex flex-col items-center justify-center px-6">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none" />
      
      {/* Lock icon */}
      <div className="relative mb-8">
        <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center">
          <Icon className="w-12 h-12 text-primary" />
        </div>
        <div className="absolute -bottom-2 -right-2 w-10 h-10 rounded-full bg-background border-4 border-background flex items-center justify-center">
          <ShieldCheck className="w-5 h-5 text-success" />
        </div>
      </div>

      {/* Title */}
      <h1 className="text-2xl font-bold mb-2">EasyFinder</h1>
      <p className="text-muted-foreground text-center mb-8">
        Utilisez {getBiometryName()} pour accéder à vos documents
      </p>

      {/* Error message */}
      {error && (
        <p className="text-destructive text-sm mb-4 text-center">
          {error}
        </p>
      )}

      {/* Auth button */}
      <Button
        size="xl"
        onClick={handleAuthenticate}
        disabled={isAuthenticating}
        className="w-full max-w-xs gap-3"
      >
        <Icon className="w-5 h-5" />
        {isAuthenticating ? "Vérification..." : `Utiliser ${getBiometryName()}`}
      </Button>

      {/* Skip option */}
      {onSkip && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onSkip}
          className="mt-4 text-muted-foreground"
        >
          <Lock className="w-4 h-4 mr-2" />
          Utiliser le code
        </Button>
      )}
    </div>
  );
}
